import { useCallback, useEffect, useState } from 'react';
import { problemsApi } from '../api/problemsApi';

/**
 * Pinning the current problem to the learner's review list.
 *
 * The state lives in a hook rather than in the button because feature 023 gives the action TWO
 * controls that must agree: the status-line pin under the frame on desktop, and a full-width pin
 * button in the decision panel on phones (where the status line is `display:none`). Only one is ever
 * on screen, but a rotation mid-verdict swaps which — so "already pinned" cannot belong to either.
 *
 * It remembers WHICH problems are pinned, not merely that the one on screen is. A single boolean
 * reset on every change of problem was enough while the session only ever moved forward; once
 * "Previous" could bring a problem back (client request, 2026-08-03), that boolean made a pinned
 * problem return offering "Add to Review" again — inviting a second pin of something already in the
 * list (client report). Membership of a set answers the question for whichever problem is on screen,
 * whichever way the learner arrived at it, and needs no reset: a problem never pinned is simply not
 * in the set.
 *
 * The set is seeded from the SERVER's list on mount (`GET /problems/review/ids`), so a problem
 * pinned weeks ago opens as "In Review" too, not only one pinned during this visit (client request,
 * 2026-08-03). Ids rather than the problems themselves, and one call rather than one per problem:
 * membership is the whole question, and it is asked again at every step through a session.
 *
 * Removal is not mirrored here, and does not need to be: "Remove from Review" belongs to
 * review-problems mode, whose SESSION_ORIGIN_CONFIG sets `allowAddToReview: false`. The two actions
 * never share a page, so this set is never on screen when something is being removed from it.
 *
 * @param problemId The problem currently on screen, or undefined when there is none.
 * @param options.enabled Whether this mode can pin at all (`allowAddToReview`). False skips the
 *   fetch — most practice modes never draw the pin, and the list is no use to them.
 */
export function useAddToReview(problemId: number | undefined, options?: { enabled?: boolean }) {
  const enabled = options?.enabled ?? true;

  const [pinnedIds, setPinnedIds] = useState<ReadonlySet<number>>(() => new Set<number>());
  // Which problem's pin is in flight, rather than a bare boolean: the request outlives a step
  // away from the problem, and only the problem that owns it should show as busy.
  const [pinningId, setPinningId] = useState<number | null>(null);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    problemsApi
      .getReviewProblemIds()
      .then((ids) => {
        if (cancelled) return;
        // Merged into what is already there, never assigned over it: a pin made while this call
        // was in flight is newer than the response and must survive it.
        setPinnedIds((prev) => {
          const merged = new Set(prev);
          for (const id of ids) merged.add(id);
          return merged;
        });
      })
      .catch((err) => {
        // Unreachable, not empty. Falling back to this visit's own pins is the old behaviour and
        // still correct as far as it goes; pinning keeps working, and re-pinning something already
        // in the list is a no-op server-side (ON CONFLICT DO NOTHING).
        console.error('Failed to load review list:', err);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const isPinned = problemId !== undefined && pinnedIds.has(problemId);
  const isPinning = problemId !== undefined && pinningId === problemId;

  const addToReview = useCallback(async () => {
    if (!problemId || isPinning || isPinned) return;
    setPinningId(problemId);
    try {
      await problemsApi.addToReview(problemId);
      setPinnedIds((prev) => new Set(prev).add(problemId));
    } catch (err) {
      console.error('Failed to add to review:', err);
    } finally {
      // Cleared only if this problem's own request is still the one outstanding.
      setPinningId((current) => (current === problemId ? null : current));
    }
  }, [problemId, isPinning, isPinned]);

  return { isPinned, isPinning, addToReview };
}
